import { useMe } from '@/features/authentication/_hooks/me';
import type { FilterConditionsModel } from '@/features/search/_models/local-storage';
import { generateHistoryDateTime } from '@/features/search/_utils/local-storage';
import { URL_PATH } from '@/utils/route-path';
import { generateSearchQuery } from '@/utils/search-params';
import { isEqual } from 'lodash-es';
import { useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import type { z } from 'zod';
import {
  type CommentFilterHistoryStorageModel,
  CommentSearchAccordionOpenSettingModel,
  type CommentSearchAccordionOpenSettingValueModel,
} from '../models/local-storage';
import type { CommentFilterParams } from '../models/search';
import {
  COMMENT_FILTER_SIDER_KEY,
  COMMENT_SEARCH_ACCORDION_OPEN_SETTING_KEY,
  COMMENT_SEARCH_HISTORY_KEY,
} from '../utils/storage-keys';

export const useCommentSearchAccordionOpenSetting = (userId: string) => {
  const [
    commentSearchAccordionOpenSetting,
    setCommentSearchAccordionOpenSetting,
  ] = useLocalStorageState<
    z.infer<typeof CommentSearchAccordionOpenSettingModel>
  >(COMMENT_SEARCH_ACCORDION_OPEN_SETTING_KEY);

  const commentSearchAccordionOpenSettingParseResult =
    CommentSearchAccordionOpenSettingModel.safeParse(
      commentSearchAccordionOpenSetting,
    );
  const parsedCommentSearchAccordionOpenSetting =
    commentSearchAccordionOpenSettingParseResult.success
      ? commentSearchAccordionOpenSettingParseResult.data
      : null;

  const searchAccordionOpenSettingByUser =
    parsedCommentSearchAccordionOpenSetting?.[userId];

  return {
    commentSearchAccordionOpenSetting: searchAccordionOpenSettingByUser ?? {
      comment: true,
    },
    setCommentSearchAccordionOpenSetting: (
      values: z.infer<typeof CommentSearchAccordionOpenSettingValueModel>,
    ) => {
      setCommentSearchAccordionOpenSetting((localStorageValue) => ({
        ...localStorageValue,
        [userId]: values,
      }));
    },
  };
};

export const useLastCommentSearchHistory = () => {
  const { data } = useMe();

  const userId = data?.id ?? '';

  type State = z.infer<typeof CommentFilterHistoryStorageModel>;

  const [searchHistoryStorage, setSearchHistoryStorage] =
    useLocalStorageState<State>(COMMENT_SEARCH_HISTORY_KEY);

  const lastSearched = searchHistoryStorage?.[userId]?.at(-1)?.filterConditions;

  const commentPath = useMemo(() => {
    return generateSearchQuery({
      lastSearched,
      defaultPath: URL_PATH.comment,
    });
  }, [lastSearched]);

  return {
    lastSearched,
    commentPathWithQuery: commentPath,
    setSearchHistoryStorage: (
      filterConditions: z.infer<typeof FilterConditionsModel>,
    ) => {
      setSearchHistoryStorage((prevValue) => {
        let searchHistory = [...(prevValue?.[userId] ?? [])];

        if (!isEqual(filterConditions, lastSearched)) {
          searchHistory = [
            {
              filterConditions,
              datetime: generateHistoryDateTime(),
            },
          ];
        }

        return {
          ...prevValue,
          [userId]: searchHistory,
        } as State;
      });
    },
  };
};

export const useSiderCommentFilterStorage = () => {
  const { data } = useMe();
  const userId = data?.id ?? '';
  const key = COMMENT_FILTER_SIDER_KEY;
  const [searchHistoryStorage, setSearchHistoryStorage] =
    useLocalStorageState<CommentFilterParams>(key);
  const lastSearched: CommentFilterParams | undefined =
    searchHistoryStorage?.[userId]?.at(-1)?.filterConditions;

  return {
    lastSearched,
    setSearchHistoryStorage: (filterConditions: CommentFilterParams) => {
      setSearchHistoryStorage((prevValue) => {
        let searchHistory = [...(prevValue?.[userId] ?? [])];

        if (!isEqual(filterConditions, lastSearched)) {
          searchHistory = [
            {
              filterConditions,
              datetime: generateHistoryDateTime(),
            },
          ];
        }

        return {
          ...prevValue,
          [userId]: searchHistory,
        } as CommentFilterParams;
      });
    },
  };
};
